import { useQuery } from '@tanstack/react-query';
import apiClient from '../utils/apiClient';
import { useBranches } from './useBranches';
import { useTimeFrame } from '../contexts/TimeFrameContext';

// Query keys for individual HR KPIs
export const HR_KPI_KEYS = {
  AVG_SATISFACTION: 'hrAvgSatisfaction',
  TOTAL_STAFF: 'hrTotalStaff',
  TOP_PERFORMERS: 'hrTopPerformers',
} as const;

// Convert time frame to months
const timeFrameToMonths = (timeFrame: string): number => {
  switch (timeFrame) {
    case 'last1Month': return 1;
    case 'last3Months': return 3;
    case 'last6Months': return 6;
    case 'lastYear': return 12;
    default: return 6;
  }
};

// API service functions for individual KPIs
const apiService = {
  fetchAverageSatisfaction: async (branchId: number, months: number) => {
    const response = await apiClient.get(`/hr/${branchId}/kpis/average-satisfaction`, {
      params: { months }
    });
    
    if (response.data && response.data.success) {
      return response.data.data;
    } else {
      throw new Error('Failed to fetch average satisfaction data');
    }
  },

  fetchTotalStaff: async (branchId: number, months: number) => {
    const response = await apiClient.get(`/hr/${branchId}/kpis/total-staff`, {
      params: { months }
    });
    
    if (response.data && response.data.success) {
      return response.data.data;
    } else {
      throw new Error('Failed to fetch total staff data');
    }
  },

  fetchTopPerformersCount: async (branchId: number, months: number) => {
    const response = await apiClient.get(`/hr/${branchId}/kpis/top-performers-count`, {
      params: { months }
    });
    
    if (response.data && response.data.success) {
      return response.data.data;
    } else {
      throw new Error('Failed to fetch top performers count');
    }
  },
};

// Hook for average satisfaction KPI
export function useHRAverageSatisfaction(branchId?: number, timeRange?: string) {
  const { selectedBranch } = useBranches(1);
  const { timeFrame } = useTimeFrame();
  const effectiveTimeRange = timeRange || timeFrame;
  const months = timeFrameToMonths(effectiveTimeRange);
  const effectiveBranchId = branchId || selectedBranch;

  return useQuery({
    queryKey: [HR_KPI_KEYS.AVG_SATISFACTION, effectiveBranchId, months],
    queryFn: async () => {
      if (!effectiveBranchId) throw new Error('Branch ID is required');
      return await apiService.fetchAverageSatisfaction(effectiveBranchId, months);
    },
    enabled: !!effectiveBranchId,
    retry: 1,
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
}

// Hook for total staff KPI
export function useHRTotalStaff(branchId?: number, timeRange?: string) {
  const { selectedBranch } = useBranches(1);
  const { timeFrame } = useTimeFrame();
  const effectiveTimeRange = timeRange || timeFrame;
  const months = timeFrameToMonths(effectiveTimeRange);
  const effectiveBranchId = branchId || selectedBranch;

  return useQuery({
    queryKey: [HR_KPI_KEYS.TOTAL_STAFF, effectiveBranchId, months],
    queryFn: async () => {
      if (!effectiveBranchId) throw new Error('Branch ID is required');
      return await apiService.fetchTotalStaff(effectiveBranchId, months);
    },
    enabled: !!effectiveBranchId,
    retry: 1,
    staleTime: 2 * 60 * 1000,
  });
}

// Hook for top performers count KPI
export function useHRTopPerformersCount(branchId?: number, timeRange?: string) {
  const { selectedBranch } = useBranches(1);
  const { timeFrame } = useTimeFrame();
  const effectiveTimeRange = timeRange || timeFrame;
  const months = timeFrameToMonths(effectiveTimeRange);
  const effectiveBranchId = branchId || selectedBranch;

  return useQuery({
    queryKey: [HR_KPI_KEYS.TOP_PERFORMERS, effectiveBranchId, months],
    queryFn: async () => {
      if (!effectiveBranchId) throw new Error('Branch ID is required');
      return await apiService.fetchTopPerformersCount(effectiveBranchId, months);
    },
    enabled: !!effectiveBranchId,
    retry: 1,
    staleTime: 2 * 60 * 1000,
  });
}